// components/MyTrainings/MyTrainings.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { trainingService } from '../../services/trainingService';
import './MyTrainings.css';

const MyTrainings = () => {
  const { user } = useAuth();
  const [trainings, setTrainings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  
  // Модальные окна
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [selectedTraining, setSelectedTraining] = useState(null);
  const [completedDate, setCompletedDate] = useState(new Date().toISOString().split('T')[0]);
  const [certificateNumber, setCertificateNumber] = useState('');
  const [regulatoryActs, setRegulatoryActs] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const showToast = useCallback((message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
  }, []);

  const calculateDaysLeft = (expiresDateString) => {
    if (!expiresDateString) return Infinity;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expireDate = new Date(expiresDateString);
    expireDate.setHours(0, 0, 0, 0);
    return Math.ceil((expireDate - today) / (1000 * 60 * 60 * 24));
  };

  const fetchMyTrainings = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Получаем ID сотрудника из данных пользователя
      const employeeId = user?.employee_id;
      
      if (!employeeId) {
        setError('Не удалось определить сотрудника');
        setLoading(false);
        return;
      }
      
      const response = await trainingService.getByEmployee(employeeId);
      
      let trainingsArray = [];
      if (Array.isArray(response.trainings)) {
        trainingsArray = response.trainings;
      } else if (Array.isArray(response.data)) {
        trainingsArray = response.data;
      } else if (Array.isArray(response)) {
        trainingsArray = response;
      }
      
      const normalizedTrainings = trainingsArray.map(t => {
        const expiresDate = t.expiresDate || t.expiration_date || t.expires_date;
        const completedDateValue = t.completedDate || t.completed_date;
        const daysLeft = calculateDaysLeft(expiresDate);
        
        let status = 'active';
        if (completedDateValue) {
          status = 'completed';
        } else if (daysLeft <= 0) {
          status = 'expired';
        } else if (daysLeft <= 30) {
          status = 'warning';
        }
        
        return {
          id: t.id,
          name: t.name || t.course_name,
          course_id: t.course_id || t.courseId,
          assignedDate: t.assignedDate || t.assigned_date,
          completedDate: completedDateValue,
          expiresDate: expiresDate,
          certificateUrl: t.certificateUrl || t.certificate_url,
          certificateNumber: t.certificateNumber || t.certificate_number || null,
          regulatoryActs: t.regulatoryActs || t.regulatory_acts || null,
          daysLeft: daysLeft,
          status: status
        };
      });
      
      setTrainings(normalizedTrainings);
    } catch (err) {
      console.error('Ошибка загрузки обучений:', err);
      setError(err.message || 'Не удалось загрузить данные об обучении');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchMyTrainings();
  }, [fetchMyTrainings]);

  const getStatusClass = (training) => {
    if (training.completedDate) return 'completed';
    if (training.daysLeft <= 0) return 'expired';
    if (training.daysLeft <= 30) return 'warning';
    return 'active';
  };

  const getStatusText = (training) => {
    if (training.completedDate) return 'Пройдено';
    if (training.daysLeft <= 0) return 'Просрочено';
    if (training.daysLeft <= 30) return 'Истекает';
    return 'Активно';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
      return new Date(dateString).toLocaleDateString('ru-RU');
    } catch {
      return dateString;
    }
  };

  const handleOpenCompleteModal = (training) => {
    setSelectedTraining(training);
    setCompletedDate(new Date().toISOString().split('T')[0]);
    setCertificateNumber(training.certificateNumber || '');
    setRegulatoryActs(training.regulatoryActs || '');
    setShowCompleteModal(true);
  };

  const handleCompleteTraining = async () => {
    if (!selectedTraining) return;
    
    setSubmitting(true);
    try {
      await trainingService.complete(selectedTraining.id, {
        completed_date: completedDate,
        certificate_number: certificateNumber,
        regulatory_acts: regulatoryActs
      });
      
      showToast(`Обучение "${selectedTraining.name}" отмечено как пройденное`, 'success');
      setShowCompleteModal(false);
      setSelectedTraining(null);
      setCertificateNumber('');
      setRegulatoryActs('');
      await fetchMyTrainings();
    } catch (err) {
      console.error('Ошибка завершения обучения:', err);
      showToast(`Ошибка: ${err.message || 'Не удалось отметить обучение'}`, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatistics = () => {
    const total = trainings.length;
    const completed = trainings.filter(t => t.completedDate).length;
    const expired = trainings.filter(t => !t.completedDate && t.daysLeft <= 0).length;
    const expiring = trainings.filter(t => !t.completedDate && t.daysLeft > 0 && t.daysLeft <= 30).length;
    const active = total - completed - expired - expiring;
    const compliance = total > 0 ? Math.round((completed / total) * 100) : 0;
    
    return { total, active, completed, expired, expiring, compliance };
  };

  if (loading) {
    return (
      <div className="my-trainings loading">
        <div className="spinner"></div>
        <p>Загрузка ваших обучений...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="my-trainings error">
        <div className="error-icon">⚠️</div>
        <p>{error}</p>
        <button className="btn-primary" onClick={fetchMyTrainings}>Повторить</button>
      </div>
    );
  }

  const stats = getStatistics();

  return (
    <div className="my-trainings">
      {toast.show && (
        <div className={`toast-notification ${toast.type}`}>
          <span className="toast-icon">{toast.type === 'success' ? '✓' : '⚠️'}</span>
          <span className="toast-message">{toast.message}</span>
        </div>
      )}

      <div className="trainings-header">
        <h1>Мои обучения</h1>
        <button className="btn-refresh" onClick={fetchMyTrainings} title="Обновить">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 12a9 9 0 0 1-9 9 9 9 0 0 1 0-18c2.5 0 4.75 1 6.38 2.62" />
            <path d="M21 3v6h-6" />
          </svg>
        </button>
      </div>

      {/* Статистика */}
      <div className="stats-cards">
        <div className="stat-card active">
          <div className="stat-value">{stats.active}</div>
          <div className="stat-label">Активных</div>
        </div>
        <div className="stat-card completed">
          <div className="stat-value">{stats.completed}</div>
          <div className="stat-label">Пройдено</div>
        </div>
        <div className="stat-card expired">
          <div className="stat-value">{stats.expired}</div>
          <div className="stat-label">Просрочено</div>
        </div>
        <div className="stat-card expiring">
          <div className="stat-value">{stats.expiring}</div>
          <div className="stat-label">Истекают (30 дней)</div>
        </div>
      </div>

      {/* Прогресс обучения */}
      <div className="progress-section">
        <div className="progress-header">
          <span>Общий прогресс обучения</span>
          <span className="progress-percent">{stats.compliance}%</span>
        </div>
        <div className="progress-bar">
          <div className="progress-fill" style={{ width: `${stats.compliance}%` }} />
        </div>
      </div>

      {/* Таблица обучений */}
      <div className="trainings-table-wrapper">
        {trainings.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📚</div>
            <div className="empty-text">Нет назначенных обучений</div>
            <div className="empty-subtext">Вам пока не назначены курсы</div>
          </div>
        ) : (
          <table className="trainings-table">
            <thead>
              <tr>
                <th>Название курса</th>
                <th>Статус</th>
                <th>Дата назначения</th>
                <th>Дата прохождения</th>
                <th>Действует до</th>
                <th>№ удостоверения</th>
                <th>Сертификат</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {trainings.map((training) => (
                <tr key={training.id}>
                  <td className="course-name">{training.name}</td>
                  <td>
                    <span className={`status-badge ${getStatusClass(training)}`}>
                      {getStatusText(training)}
                    </span>
                  </td>
                  <td>{formatDate(training.assignedDate)}</td>
                  <td>{formatDate(training.completedDate)}</td>
                  <td>{formatDate(training.expiresDate)}</td>
                  <td>
                    {training.certificateNumber || '—'}
                  </td>
                  <td>
                    {training.certificateUrl ? (
                      <a href={training.certificateUrl} target="_blank" rel="noopener noreferrer" className="certificate-link">
                        Скачать
                      </a>
                    ) : '—'}
                  </td>
                  <td>
                    {!training.completedDate && training.daysLeft > 0 && (
                      <button 
                        className="btn-complete" 
                        onClick={() => handleOpenCompleteModal(training)}
                        title="Отметить как пройденное"
                      >
                        Отметить пройденным
                      </button>
                    )}
                    {training.completedDate && (
                      <button 
                        className="btn-edit-metadata" 
                        onClick={() => handleOpenCompleteModal(training)}
                        title="Редактировать данные"
                      >
                        Редактировать
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Модальное окно завершения/редактирования обучения */}
      {showCompleteModal && selectedTraining && (
        <div className="modal-overlay" onClick={() => setShowCompleteModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{selectedTraining.completedDate ? 'Редактировать данные обучения' : 'Отметить обучение как пройденное'}</h3>
              <button className="modal-close" onClick={() => setShowCompleteModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p><strong>Курс:</strong> {selectedTraining.name}</p>
              
              <div className="form-group">
                <label>Дата прохождения *</label>
                <input 
                  type="date" 
                  value={completedDate} 
                  onChange={(e) => setCompletedDate(e.target.value)} 
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label>№ удостоверения</label>
                <input 
                  type="text" 
                  value={certificateNumber}
                  onChange={(e) => setCertificateNumber(e.target.value)}
                  className="form-input"
                  placeholder="Введите номер удостоверения"
                />
                <small className="form-hint">Укажите номер удостоверения (если есть)</small>
              </div>

              <div className="form-group">
                <label>НПА (нормативно-правовой акт)</label>
                <textarea
                  value={regulatoryActs}
                  onChange={(e) => setRegulatoryActs(e.target.value)}
                  className="form-input"
                  rows="3"
                  placeholder="Введите НПА, на основании которого проводится обучение"
                />
                <small className="form-hint">Например: Приказ Минтруда №123 от 01.01.2024</small>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowCompleteModal(false)}>Отмена</button>
              <button className="btn-submit" onClick={handleCompleteTraining} disabled={submitting}>
                {submitting ? 'Сохранение...' : (selectedTraining.completedDate ? 'Сохранить' : 'Отметить')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyTrainings;